import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, Search, ChevronDown } from "lucide-react";
import { useAuth, useCart } from "App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { cartItemCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Shop", path: "/shop" },
    // { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };
  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 -ml-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-[#4F3535]" />
            </button>

            {/* Logo */}
            <Link
              to="/"
              className="flex-shrink-0 flex items-center"
              data-testid="logo-link"
            >
              <img
                src="/assets/logo.png"
                alt="Jojo's Boutique Logo"
                className="h-12 md:h-16 object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm tracking-widest uppercase transition-colors ${
                    isActive(link.path)
                      ? "text-[#4F3535] font-semibold"
                      : "text-[#8A7B7B] hover:text-[#4F3535]"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(" ", "-")}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Side Icons */}
            <div className="flex items-center gap-4 md:gap-6">
              {/* Search (Placeholder) */}
              <button className="p-2 text-[#4F3535] hover:text-[#6A4B4B] transition-colors">
                <Search className="w-5 h-5" />
              </button>

              {/* Cart */}
              <Link
                to="/cart"
                className="relative p-2 text-[#4F3535] hover:text-[#6A4B4B] transition-colors"
                data-testid="cart-link"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#6A4B4B] text-white text-[10px] rounded-full flex items-center justify-center cart-badge">
                    {cartItemCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 p-2 ml-2"
                      data-testid="user-menu-btn"
                    >
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-7 h-7 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <User className="w-5 h-5 text-[#4F3535]" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-48 bg-[#F9F6F2] border-[#EAE2DA]"
                  >
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium truncate text-[#4F3535]">
                        {user.name}
                      </p>
                      <p className="text-xs text-[#8A7B7B] truncate">
                        {user.email}
                      </p>
                    </div>
                    <DropdownMenuSeparator className="bg-[#EAE2DA]" />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/orders"
                        className="cursor-pointer hover:bg-[#FAF7F3] focus:bg-[#FAF7F3]"
                        data-testid="my-orders-link"
                      >
                        My Orders
                      </Link>
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/admin"
                          className="cursor-pointer hover:bg-[#FAF7F3] focus:bg-[#FAF7F3]"
                          data-testid="admin-link"
                        >
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-[#EAE2DA]" />
                    <DropdownMenuItem
                      onClick={logout}
                      className="cursor-pointer text-[#6A4B4B] hover:bg-[#FAF7F3] focus:bg-[#FAF7F3]"
                      data-testid="logout-btn"
                    >
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  onClick={login}
                  className="hidden md:flex items-center p-2 text-[#4F3535] hover:text-[#6A4B4B] transition-colors ml-2"
                  data-testid="sign-in-nav-btn"
                  title="Sign In"
                >
                  <User className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-50 md:hidden backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 left-0 bottom-0 w-80 bg-[#F9F6F2] z-50 md:hidden shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-12">
                  <img
                    src="/assets/logo.png"
                    alt="Jojo's Boutique Logo"
                    className="h-10 object-contain"
                  />
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="close-menu-btn"
                  >
                    <X className="w-6 h-6 text-[#4F3535]" />
                  </button>
                </div>

                <div className="space-y-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block py-4 text-sm tracking-widest uppercase border-b border-[#EAE2DA] ${
                        isActive(link.path)
                          ? "text-[#4F3535] font-semibold"
                          : "text-[#8A7B7B]"
                      }`}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(" ", "-")}`}
                    >
                      {link.name}
                    </Link>
                  ))}

                  {user ? (
                    <>
                      <Link
                        to="/orders"
                        className="block py-4 text-sm tracking-widest uppercase border-b border-[#EAE2DA] text-[#8A7B7B]"
                      >
                        My Orders
                      </Link>
                      {user.is_admin && (
                        <Link
                          to="/admin"
                          className="block py-4 text-sm tracking-widest uppercase border-b border-[#EAE2DA] text-[#8A7B7B]"
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={logout}
                        className="block w-full text-left py-4 text-sm tracking-widest uppercase text-[#6A4B4B]"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={login}
                      className="w-full btn-primary mt-8"
                      data-testid="mobile-sign-in-btn"
                    >
                      Sign In with Google
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
